/**
 * KgEntityTabs — tabbed extension for any Knowledge Graph entity
 * detail page. Provides four views:
 *  1. Overview  — the AI-authored profile (rendered by the parent)
 *  2. News      — latest news matching the entity name + aliases
 *  3. Media     — user-contributed photos and video links
 *  4. Discussion — threaded comments
 *
 * The parent renders `Overview` inline; this component owns the tab
 * bar and the other three panels.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Newspaper, Image as ImageIcon, MessagesSquare, PenLine, Send,
  ExternalLink, Loader2, PlusCircle, User, Trash2, Video,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const TABS = [
  { key: "overview",   label: "Overview",   icon: PenLine       },
  { key: "news",       label: "News",       icon: Newspaper     },
  { key: "media",      label: "Photos & Videos", icon: ImageIcon},
  { key: "discussion", label: "Discussion", icon: MessagesSquare},
];

export default function KgEntityTabs({ entity, children }) {
  const [active, setActive] = useState("overview");

  if (!entity) return children;

  return (
    <div className="mt-4" data-testid="kg-entity-tabs">
      <div className="flex border-b overflow-x-auto" style={{ borderColor: "var(--wofy-border)" }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 transition ${
              active === key
                ? "font-semibold"
                : "opacity-70 hover:opacity-100 border-transparent"
            }`}
            style={{ borderColor: active === key ? "var(--wofy-primary)" : "transparent" }}
            data-testid={`kg-tab-${key}`}
          >
            <Icon className="w-4 h-4"/> {label}
          </button>
        ))}
      </div>

      <div className="pt-6">
        {active === "overview"   && children}
        {active === "news"       && <EntityNewsPanel entity={entity} />}
        {active === "media"      && <EntityMediaPanel entity={entity} />}
        {active === "discussion" && <EntityDiscussionPanel entity={entity} />}
      </div>
    </div>
  );
}

/* ─── News panel ────────────────────────────────────────────────── */
function EntityNewsPanel({ entity }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get(`/kg/entities/${entity.slug}/news`, { params: { limit: 12 } })
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [entity.slug]);

  if (loading) return <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin"/>Loading news…</div>;
  if (items.length === 0) return <div className="wofy-card !p-6 text-center opacity-70">No news mentions found yet. As stories mention <strong>{entity.name}</strong>, they'll show up here automatically.</div>;

  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="entity-news-panel">
      {items.map(n => (
        <a
          key={n.id || n.url}
          href={n.url}
          target="_blank" rel="noopener noreferrer"
          className="wofy-card !p-3 hover:shadow-md transition block group"
          data-testid="entity-news-item"
        >
          <div className="flex items-start gap-3">
            {n.image_url && <img src={n.image_url} alt="" className="w-16 h-16 object-cover rounded-md shrink-0" loading="lazy"/>}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1 truncate">
                {n.source_name || n.category || "News"} · {relTime(n.published_at)}
              </div>
              <div className="text-sm font-medium leading-snug group-hover:underline">{n.title}</div>
              {n.snippet && <p className="text-xs opacity-70 mt-1 line-clamp-2">{n.snippet}</p>}
            </div>
            <ExternalLink className="w-3.5 h-3.5 opacity-40 shrink-0 mt-1"/>
          </div>
        </a>
      ))}
    </div>
  );
}

/* ─── Media panel ───────────────────────────────────────────────── */
const YT_RE  = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/;
const VIM_RE = /vimeo\.com\/(\d+)/;
const embedFor = (url) => {
  const y = url.match(YT_RE); if (y) return `https://www.youtube.com/embed/${y[1]}`;
  const v = url.match(VIM_RE); if (v) return `https://player.vimeo.com/video/${v[1]}`;
  return null;
};

function EntityMediaPanel({ entity }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [kind, setKind] = useState("image");
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [credit, setCredit] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/kg/entities/${entity.slug}/media`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [entity.slug]);

  const images = items.filter(i => i.kind === "image");
  const videos = items.filter(i => i.kind === "video");

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to contribute."); return; }
    if (!url.trim()) return;
    if (kind === "video" && !embedFor(url.trim())) {
      toast.error("Please provide a YouTube or Vimeo link.");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/kg/entities/${entity.slug}/media`, {
        kind, url: url.trim(), caption: caption.trim(), credit: credit.trim(),
      });
      toast.success("Thanks for contributing!");
      setUrl(""); setCaption(""); setCredit(""); setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="entity-media-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm opacity-80">Community-contributed photos and videos.</div>
        <button onClick={() => setShowAdd(v => !v)}
          className="wofy-btn-primary text-sm inline-flex items-center gap-1.5"
          data-testid="media-add-toggle">
          <PlusCircle className="w-4 h-4"/>Contribute
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="wofy-card !p-4 mb-4 grid gap-2" data-testid="media-add-form">
          <div className="flex gap-2 items-center text-sm">
            <label className="inline-flex items-center gap-1"><input type="radio" name="kind" checked={kind === "image"} onChange={() => setKind("image")}/> Photo</label>
            <label className="inline-flex items-center gap-1"><input type="radio" name="kind" checked={kind === "video"} onChange={() => setKind("video")}/> Video link</label>
          </div>
          <input value={url} onChange={e => setUrl(e.target.value)} required
            placeholder={kind === "image" ? "Public image URL (https://…)" : "YouTube or Vimeo URL"}
            className="text-sm px-2 py-2 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="media-url-input"/>
          <input value={caption} onChange={e => setCaption(e.target.value)}
            placeholder="Caption (optional)"
            className="text-sm px-2 py-2 rounded border" style={{ borderColor: "var(--wofy-border)" }}/>
          <input value={credit} onChange={e => setCredit(e.target.value)}
            placeholder="Photo / video credit (optional)"
            className="text-sm px-2 py-2 rounded border" style={{ borderColor: "var(--wofy-border)" }}/>
          <button disabled={busy} className="wofy-btn-primary text-sm inline-flex items-center gap-1.5 self-start"
            data-testid="media-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            Submit
          </button>
        </form>
      )}

      {loading ? (
        <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
      ) : items.length === 0 ? (
        <div className="wofy-card !p-6 text-center opacity-80">
          No photos or videos yet. Be the first to contribute one of <strong>{entity.name}</strong>.
        </div>
      ) : (
        <>
          {videos.length > 0 && (
            <section className="mb-6">
              <h3 className="font-display text-lg mb-3 inline-flex items-center gap-1.5"><Video className="w-4 h-4"/>Videos ({videos.length})</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {videos.map(v => {
                  const src = embedFor(v.url);
                  return (
                    <div key={v.id} className="wofy-card !p-0 overflow-hidden" data-testid="media-video-item">
                      {src ? (
                        <div className="aspect-video">
                          <iframe src={src} title={v.caption || entity.name} className="w-full h-full" allowFullScreen loading="lazy"/>
                        </div>
                      ) : (
                        <a href={v.url} target="_blank" rel="noopener noreferrer" className="block p-4">{v.url}</a>
                      )}
                      {(v.caption || v.credit) && (
                        <div className="p-3 text-xs opacity-80">
                          {v.caption && <div>{v.caption}</div>}
                          {v.credit && <div className="opacity-60">— {v.credit}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          {images.length > 0 && (
            <section>
              <h3 className="font-display text-lg mb-3 inline-flex items-center gap-1.5"><ImageIcon className="w-4 h-4"/>Photos ({images.length})</h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {images.map(im => (
                  <figure key={im.id} className="wofy-card !p-2" data-testid="media-image-item">
                    <img src={im.url} alt={im.caption || ""} className="w-full h-48 object-cover rounded-md" loading="lazy"/>
                    {(im.caption || im.credit) && (
                      <figcaption className="text-xs opacity-80 mt-2 px-1">
                        {im.caption && <div>{im.caption}</div>}
                        {im.credit && <div className="opacity-60">— {im.credit}</div>}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Discussion panel ──────────────────────────────────────────── */
function EntityDiscussionPanel({ entity }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/kg/entities/${entity.slug}/discussions`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [entity.slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to comment."); return; }
    if (!text.trim()) return;
    setBusy(true);
    try {
      await http.post(`/kg/entities/${entity.slug}/discussions`, { text: text.trim() });
      setText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post.");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await http.delete(`/kg/entities/${entity.slug}/discussions/${id}`);
      load();
    } catch { toast.error("Could not delete."); }
  };

  return (
    <div data-testid="entity-discussion-panel">
      <form onSubmit={submit} className="wofy-card !p-4 mb-4 flex gap-2 items-start" data-testid="discussion-form">
        <div className="w-8 h-8 rounded-full bg-black/5 grid place-items-center shrink-0"><User className="w-4 h-4"/></div>
        <div className="flex-1">
          <textarea value={text} onChange={e => setText(e.target.value)}
            placeholder={user ? `Share what you know about ${entity.name}…` : "Sign in to join the discussion."}
            disabled={!user}
            rows={3}
            className="w-full text-sm px-3 py-2 rounded border resize-none disabled:opacity-60"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="discussion-input"/>
          <div className="mt-2 flex items-center justify-between">
            <div className="text-xs opacity-60">Be respectful and cite sources when possible. {text.length}/5000</div>
            <button disabled={!user || busy || !text.trim()}
              className="wofy-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="discussion-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              Post comment
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin"/>Loading discussion…</div>
      ) : items.length === 0 ? (
        <div className="wofy-card !p-6 text-center opacity-80">No comments yet. Start the discussion — share what you know about <strong>{entity.name}</strong>.</div>
      ) : (
        <ul className="space-y-3">
          {items.map(c => (
            <li key={c.id} className="wofy-card !p-4" data-testid="discussion-item">
              <div className="flex items-start gap-3">
                {c.user_avatar ? (
                  <img src={c.user_avatar} alt="" className="w-8 h-8 rounded-full object-cover"/>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-black/5 grid place-items-center shrink-0"><User className="w-4 h-4"/></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs opacity-70 mb-1">
                    <span className="font-medium text-[color:var(--wofy-text)]">{c.user_name}</span>
                    <span>·</span><span>{relTime(c.created_at)}</span>
                  </div>
                  <div className="text-sm whitespace-pre-line">{c.text}</div>
                </div>
                {user?.id === c.user_id && (
                  <button onClick={() => del(c.id)} className="opacity-40 hover:opacity-100" title="Delete" data-testid="discussion-delete">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─── shared ───────────────────────────────────────────────────── */
function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
